import { isValidSenegalPhone } from './phoneHelpers'

export type BulkEnrollmentExitStep =
    | 'package_selection'
    | 'phone_entry'
    | 'name_entry'
    | 'row_confirm'
    | 'batch_validation'

type BulkEnrollmentSession = {
    startedAt: number
    closed: boolean
    startedTracked: boolean
    rowStartedAt: Record<string, number>
    currentStep: BulkEnrollmentExitStep
}

let session: BulkEnrollmentSession | null = null

const defaultRow: Pick<
    { phone: string; firstname: string; lastname: string; confirmed: boolean },
    'phone' | 'firstname' | 'lastname' | 'confirmed'
> = {
    phone: '',
    firstname: '',
    lastname: '',
    confirmed: false
}

export const ensurePositiveDuration = (ms: number) => Math.max(1, ms)

const getSession = (now = Date.now()): BulkEnrollmentSession => {
    session ??= {
        startedAt: now,
        closed: false,
        startedTracked: false,
        rowStartedAt: { 1: now },
        currentStep: 'package_selection'
    }
    return session
}

export const resetBulkEnrollmentAnalyticsSession = (now = Date.now()) => {
    session = {
        startedAt: now,
        closed: false,
        startedTracked: false,
        rowStartedAt: { 1: now },
        currentStep: 'package_selection'
    }
    return session
}

export const clearBulkEnrollmentAnalyticsSession = () => {
    session = null
}

export const markBulkEnrollmentSessionCompleted = () => {
    getSession().closed = true
}

export const markBulkEnrollmentSessionAbandoned = () => {
    getSession().closed = true
}

export const isBulkEnrollmentSessionClosed = () => !session || session.closed

export const hasActiveBulkEnrollmentSession = () =>
    Boolean(session && !session.closed)

export const markBulkEnrollmentStartedTracked = () => {
    getSession().startedTracked = true
}

export const wasBulkEnrollmentStartedTracked = () =>
    Boolean(session?.startedTracked)

export const startBulkEnrollmentRowTimer = (rowId: string, now = Date.now()) => {
    getSession(now).rowStartedAt[rowId] = now
}

export const getBulkEnrollmentSessionDurationMs = (now = Date.now()) =>
    ensurePositiveDuration(now - getSession(now).startedAt)

export const getBulkEnrollmentRowDurationMs = (
    rowId: string,
    now = Date.now()
) => {
    const current = getSession(now)
    const startedAt = current.rowStartedAt[rowId] ?? current.startedAt
    return ensurePositiveDuration(now - startedAt)
}

export const setBulkEnrollmentCurrentStep = (
    step: BulkEnrollmentExitStep
) => {
    getSession().currentStep = step
}

export const getBulkEnrollmentCurrentStep = (): BulkEnrollmentExitStep =>
    getSession().currentStep

export const bulkEnrollmentExitStepFromPathname = (
    pathname: string
): BulkEnrollmentExitStep => {
    if (pathname.includes('/bulk-enrollment/confirmation')) {
        return 'batch_validation'
    }
    if (pathname.includes('/bulk-enrollment/entry')) {
        return 'phone_entry'
    }
    return 'package_selection'
}

export const bulkEnrollmentExitStepFromRow = (
    row: Partial<typeof defaultRow> = {}
): BulkEnrollmentExitStep => {
    const phone = row.phone || ''
    if (!isValidSenegalPhone(phone)) {
        return 'phone_entry'
    }
    if (!(row.firstname || '').trim() || !(row.lastname || '').trim()) {
        return 'name_entry'
    }
    return 'row_confirm'
}

export const quantityChangeDirection = (
    previous: string,
    next: string
): 'increase' | 'decrease' | null => {
    const previousQty = Number(previous)
    const nextQty = Number(next)
    if (!Number.isFinite(previousQty) || !Number.isFinite(nextQty)) {
        return null
    }
    if (next === '' || previous === '') {
        return null
    }
    if (nextQty === previousQty) {
        return null
    }
    return nextQty > previousQty ? 'increase' : 'decrease'
}

export const rowValidationErrorName = ({
    phone,
    isAgentPhone,
    isDuplicate,
    phoneSettled = true
}: {
    phone: string
    isAgentPhone: boolean
    isDuplicate: boolean
    phoneSettled?: boolean
}): 'invalid_number' | 'agent_number' | 'duplicate' | null => {
    if (!phone) {
        return null
    }
    if (isAgentPhone) {
        return 'agent_number'
    }
    if (isDuplicate) {
        return 'duplicate'
    }
    // Incomplete numbers are not errors until the field settles (blur or
    // 9+ digits). Agent / duplicate can only be true of a complete number.
    if (!isValidSenegalPhone(phone)) {
        return phoneSettled ? 'invalid_number' : null
    }
    return null
}

export const withBulkEnrollmentConnectivity = (
    name: string,
    isOnline: boolean
) => `${name}-${isOnline ? 'online' : 'offline'}`
